import { useTranslation } from 'react-i18next';
import {
  Bell,
  Zap,
  Mail,
  Clock,
  CheckCircle2,
  BarChart3,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/assist/ui/dropdown-menu';
import { Button } from '@/components/assist/ui/button';
import { cn } from '@/components/assist/lib/utils';
import { useNotifications } from '@/hooks/useNotifications';

const typeIcons: Record<string, typeof Zap> = {
  'survey.activated': Zap,
  'survey.invitations_sent': Mail,
  'survey.reminders_sent': Clock,
  'survey.completed': CheckCircle2,
  'survey.insights_ready': BarChart3,
};

const typeColors: Record<string, string> = {
  'survey.activated': 'text-green-500',
  'survey.invitations_sent': 'text-primary',
  'survey.reminders_sent': 'text-orange-500',
  'survey.completed': 'text-muted-foreground',
  'survey.insights_ready': 'text-green-500',
};

function formatTime(dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);

  if (diffMins < 1) return t('header.timeJustNow');
  if (diffMins < 60) return t('header.timeMinutesAgo', { count: diffMins });
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return t('header.timeHoursAgo', { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  return t('header.timeDaysAgo', { count: diffDays });
}

export function NotificationsDropdown() {
  const { t } = useTranslation('assist');
  const { notifications, unreadCount, markAsRead, markAllAsRead } =
    useNotifications();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-orange-500 text-[10px] font-medium text-white">
              {unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 bg-popover" align="end" forceMount>
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>{t('header.notifications')}</span>
          {unreadCount > 0 && (
            <Button
              variant="link"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                markAllAsRead();
              }}
              className="h-auto p-0 text-xs font-normal"
            >
              {t('header.markAllRead', 'Mark all read')}
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {t('header.noNotifications', 'No notifications yet')}
            </div>
          ) : (
            notifications.map((notification) => {
              const Icon = typeIcons[notification.type] ?? Bell;
              const color = typeColors[notification.type] ?? 'text-muted-foreground';
              return (
                <DropdownMenuItem
                  key={notification.id}
                  className={cn(
                    'flex items-start gap-3 p-3 cursor-pointer',
                    !notification.read && 'bg-muted/50',
                  )}
                  onClick={() => {
                    if (!notification.read) markAsRead(notification.id);
                  }}
                >
                  <div className={cn('mt-0.5 shrink-0', color)}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {notification.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {notification.body}
                    </p>
                    <p className="text-xs text-muted-foreground/70">
                      {formatTime(notification.created_at, t)}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />
                  )}
                </DropdownMenuItem>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
